import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FileCheck, Calendar, Clock, ChevronRight, Loader2, Building2, Search } from 'lucide-react';
import { quotesAPI } from '../services/api';
import { useCompanyStore } from '../context/companyStore';
import CompanyFilter from '../components/common/CompanyFilter';
import { isPastDate } from '../utils/dates';
import { formatMoney } from '../utils/format';

const statusBadges = {
  draft: { label: 'Draft', className: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300' },
  pending: { label: 'Pending Review', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300' },
  viewed: { label: 'Viewed', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
  accepted: { label: 'Approved', className: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' },
  expired: { label: 'Expired', className: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400' },
  open: { label: 'Open', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
  won: { label: 'Approved', className: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
  lost: { label: 'Closed', className: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400' }
};

export default function QuotesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastRefreshTimestamp = useRef(null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [search, setSearch] = useState(searchParams.get('search') || '');

  // Multi-company support
  const { selectedCompanyId, hasMultipleCompanies } = useCompanyStore();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['quotes', selectedCompanyId],
    queryFn: () => quotesAPI.list({ companyId: selectedCompanyId }),
  });

  // Sync status filter + search to URL so both survive back-navigation.
  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
    if (search) params.set('search', search);
    setSearchParams(params, { replace: true });
  }, [statusFilter, search, setSearchParams]);

  // Auto-refresh when returning from detail page
  useEffect(() => {
    const refreshTimestamp = location.state?.refresh;
    if (refreshTimestamp && refreshTimestamp !== lastRefreshTimestamp.current) {
      lastRefreshTimestamp.current = refreshTimestamp;
      // Restore status + search from URL (URL is source of truth post-nav)
      setStatusFilter(searchParams.get('status') || 'all');
      setSearch(searchParams.get('search') || '');
      refetch();
    }
  }, [location.state?.refresh, refetch, searchParams]);

  const cwQuotes = data?.data?.quotes || [];
  const localQuotes = data?.data?.localQuotes || [];

  // Normalize quotes to a unified format
  const allQuotes = [
    ...localQuotes.map(q => ({
      id: q.id,
      type: 'local',
      name: q.quoteName || q.name,
      docNumber: q.qwDocNumber,
      date: q.quoteDate,
      validUntil: q.validUntil,
      total: q.total,
      status: getLocalStatus(q),
      createdAt: q.createdAt,
      company: q.company
    })),
    ...cwQuotes.map(q => ({
      id: q.id,
      type: 'cw',
      name: q.name,
      docNumber: null,
      date: q.expectedCloseDate,
      validUntil: null,
      total: null,
      status: getCwStatus(q),
      createdAt: q._info?.lastUpdated,
      company: q.company
    }))
  ];

  function getLocalStatus(quote) {
    const isExpired = quote.validUntil && isPastDate(quote.validUntil);
    if (isExpired && quote.status !== 'accepted' && quote.status !== 'rejected') {
      return 'expired';
    }
    return quote.status || 'pending';
  }

  function getCwStatus(quote) {
    const statusName = quote.status?.name?.toLowerCase() || 'open';
    // Check for won/accepted statuses (includes "Accepted from Portal")
    if (statusName === 'won' || statusName.includes('accepted')) return 'won';
    // Check for lost/rejected statuses
    if (statusName === 'lost' || statusName === 'inactive' || statusName.includes('rejected')) return 'lost';
    return 'open';
  }

  // Filter by status and search
  const filteredQuotes = allQuotes.filter(q => {
    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'pending' && !['pending', 'viewed', 'open'].includes(q.status)) return false;
      if (statusFilter === 'approved' && !['accepted', 'won'].includes(q.status)) return false;
      if (statusFilter === 'closed' && !['rejected', 'expired', 'lost'].includes(q.status)) return false;
    }
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      return q.name?.toLowerCase().includes(searchLower) ||
             q.company?.name?.toLowerCase().includes(searchLower) ||
             q.docNumber?.toLowerCase().includes(searchLower);
    }
    return true;
  });

  // Sort by date (newest first)
  filteredQuotes.sort((a, b) => {
    const dateA = new Date(a.createdAt || a.date || 0);
    const dateB = new Date(b.createdAt || b.date || 0);
    return dateB - dateA;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Handle date-only strings (YYYY-MM-DD) by adding time to avoid timezone shift
    const date = dateString.includes('T')
      ? new Date(dateString)
      : new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (!price) return null;
    return formatMoney(parseFloat(price));
  };

  const getStatusBadge = (status) => {
    return statusBadges[status] || statusBadges.pending;
  };

  const handleQuoteClick = (quote) => {
    sessionStorage.setItem('quotesReturnUrl', window.location.pathname + window.location.search);
    if (quote.type === 'local') {
      navigate(`/quotes/local/${quote.id}`);
    } else {
      navigate(`/quotes/${quote.id}`);
    }
  };

  // Calculate stats
  const pendingCount = allQuotes.filter(q =>
    ['pending', 'viewed', 'open'].includes(q.status)
  ).length;

  const approvedCount = allQuotes.filter(q =>
    ['accepted', 'won'].includes(q.status)
  ).length;

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Quotes & Proposals</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Review and approve quotes from our team</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search quotes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <CompanyFilter />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input w-full sm:w-40"
        >
          <option value="all">All Quotes</option>
          <option value="pending">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
          <div className="text-2xl font-bold text-neutral-900 dark:text-white">{allQuotes.length}</div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Total Quotes</div>
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{pendingCount}</div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Pending Review</div>
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{approvedCount}</div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Approved</div>
        </div>
      </div>

      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading quotes...</p>
          </div>
        ) : filteredQuotes.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
            <FileCheck className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
            <p>No quotes found</p>
            {(statusFilter !== 'all' || search) && (
              <button
                onClick={() => { setStatusFilter('all'); setSearch(''); }}
                className="mt-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {filteredQuotes.map((quote) => {
              const badge = getStatusBadge(quote.status);

              return (
                <div
                  key={`${quote.type}-${quote.id}`}
                  onClick={() => handleQuoteClick(quote)}
                  className="flex items-center justify-between p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-medium text-neutral-900 dark:text-white truncate">
                        {quote.name}
                      </p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
                        {badge.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
                      {quote.docNumber && (
                        <span>#{quote.docNumber}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(quote.date)}
                      </span>
                      {quote.validUntil && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          Valid until {formatDate(quote.validUntil)}
                        </span>
                      )}
                      {hasMultipleCompanies && quote.company?.name && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {quote.company.name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 ml-4">
                    {quote.total && (
                      <div className="text-right">
                        <div className="font-semibold text-neutral-900 dark:text-white">
                          {formatPrice(quote.total)}
                        </div>
                      </div>
                    )}
                    <ChevronRight className="w-5 h-5 text-neutral-400" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
